import React from 'react';
import { Redirect, Switch } from 'react-router-dom';
import { Application, Module } from '@fides/fides-react-components';
import { useGlobalConfigContext } from 'core/contexts/GlobalConfig';
import { PrivateRoute } from '../helpers';

const Clients = React.lazy(() => import('../../components/pages/Client'));
const PendingPaymentApprovals = React.lazy(() => import('../../components/pages/PendingApprovals/Payments'));
const PendingLiquidityMgmtApprovals = React.lazy(() => import('../../components/pages/PendingApprovals/LiquidityMgmt'));
const PendingPaymentApprovalDetails = React.lazy(
  () => import('../../components/pages/PendingApprovalDetails/Payments')
);
const PendingLiquidityMgmtApprovalDetails = React.lazy(
  () => import('../../components/pages/PendingApprovalDetails/LiquidityMgmt')
);

export const AdministrationRouter = () => {
  const { accessHelper, twoMRHelper } = useGlobalConfigContext();
  const hasAccessToClients =
    !accessHelper.isImpersonated &&
    (accessHelper.isFidesAdministrator(Application.EFTWEB) ||
      accessHelper.isFidesReadOnlyAdministrator(Application.EFTWEB));
  const hasAccessToPaymentApprovals =
    twoMRHelper.is2MREnabled &&
    (twoMRHelper.hasUserAccessToActive2MRModule ||
      (accessHelper.isImpersonatedAsClient && twoMRHelper.is2MREnabledForAnyModule));
  const hasAccessToLiquidityMgmtApprovals =
    twoMRHelper.is2MREnabled &&
    (twoMRHelper.is2MREnabledFor(Module.CASHPOOL) ||
      (accessHelper.isImpersonatedAsClient && twoMRHelper.is2MREnabledFor(Module.CASHPOOL)));

  const basePendingApprovalsPath = '/administration/pending-approvals';

  const getPendingApprovalsPath = (): string => {
    if (hasAccessToPaymentApprovals) {
      return `${basePendingApprovalsPath}/payments`;
    }
    if (hasAccessToLiquidityMgmtApprovals) {
      return `${basePendingApprovalsPath}/liquidity-management`;
    }
    return '/page-not-found';
  };

  const adminRoutes = [
    {
      path: '/administration/client',
      hasAccess: hasAccessToClients,
      component: <Clients />,
    },
    {
      path: `${basePendingApprovalsPath}/payments`,
      hasAccess: hasAccessToPaymentApprovals,
      component: <PendingPaymentApprovals />,
    },
    {
      path: `${basePendingApprovalsPath}/payments/:uniqueId`,
      hasAccess: hasAccessToPaymentApprovals,
      component: <PendingPaymentApprovalDetails />,
    },
    {
      path: `${basePendingApprovalsPath}/liquidity-management`,
      hasAccess: hasAccessToLiquidityMgmtApprovals,
      component: <PendingLiquidityMgmtApprovals />,
    },
    {
      path: `${basePendingApprovalsPath}/liquidity-management/:uniqueId`,
      hasAccess: hasAccessToLiquidityMgmtApprovals,
      component: <PendingLiquidityMgmtApprovalDetails />,
    },
  ];

  return (
    <Switch>
      <Redirect from='/administration/' to='/administration/client' exact />
      <Redirect from={basePendingApprovalsPath} to={getPendingApprovalsPath()} exact />
      {adminRoutes.map(({ path, hasAccess, component }) => (
        <PrivateRoute key={path} path={path} exact>
          {hasAccess ? component : <Redirect to='/page-not-found' />}
        </PrivateRoute>
      ))}
      <Redirect to='/page-not-found' />
    </Switch>
  );
};
